import React, { useState, useRef, useEffect } from "react";
import { Transition } from "@tailwindui/react";
import { DotsVerticalIcon, ChevronDownIcon } from "@heroicons/react/outline";

const ExpandableMenu = ({
  children,
  circle = false,
  triggerLabel,
  triggerIcon: TriggerIcon,
  showChevron = false,
  triggerClassName,
  ...props
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const circleClass = circle ? "p-2 bg-slate-100 hover:bg-slate-200 rounded-xl" : "p-1";

  useEffect(() => {
    const handleEscape = e => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };

    if (isMenuOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const handleClickOutside = event => {
    if (userMenuRef && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      setIsMenuOpen(false);
    }
  };

  const handleClickThreeDots = e => {
    // in case this is rendered in a clickable component, don't trigger parent's click handler
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(isOpen => !isOpen);
  };

  // Add null check for children
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { setIsMenuOpen });
    }
    return child;
  });

  const defaultButtonClass = `${circleClass} flex items-center text-slate-500 hover:text-slate-700 focus:border-none focus:outline-none`;

  return (
    <div className="relative inline-block text-left">
      <button
        className={triggerClassName || defaultButtonClass}
        onClick={handleClickThreeDots}
      >
        {triggerLabel ? (
          <>
            {TriggerIcon && <TriggerIcon className="h-4 w-4 mr-1" />}
            <span>{triggerLabel}</span>
            {showChevron && <ChevronDownIcon className="h-4 w-4 ml-1" />}
          </>
        ) : (
          <>
            <span className="sr-only">Open options</span>
            <DotsVerticalIcon className="h-5 w-5" data-testid={props["data-testid"]} />
          </>
        )}
      </button>

      {isMenuOpen && (
        // backdrop to prevent clicks from reaching the page
        <div
          className="fixed inset-0 bg-transparent"
          onClick={handleClickOutside}
          style={{ zIndex: 1 }}
        />
      )}

      <Transition
        show={isMenuOpen}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div
          ref={userMenuRef}
          style={{ zIndex: 2 }}
          role="menu"
          className="origin-top-right absolute right-0 mt-2 py-1 w-56 rounded-md overflow-hidden shadow-lg bg-white ring-1 ring-black ring-opacity-5"
        >
          {childrenWithProps}
        </div>
      </Transition>
    </div>
  );
};

export const ExpandableMenuButton = ({ onClick, children, setIsMenuOpen }) => {
  return (
    <button
      type="button"
      className="block focus:outline-none w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900"
      role="menuitem"
      onClick={e => {
        e.stopPropagation();
        if (onClick) onClick(e);
        setIsMenuOpen(false);
      }}
    >
      {children}
    </button>
  );
};

export default ExpandableMenu;
